/**
 * LocationPickerMap — Click-to-place interactive Leaflet mini-map.
 *
 * Provides:
 *   - Click anywhere to place/move the pin
 *   - Drag the pin to reposition
 *   - Lat/lon auto-populated and kept in sync
 *   - Theme-aware tile layer (dark → Esri Hybrid, light → CartoDB Positron)
 *   - Compact, embeddable component for the stepper and other forms
 *
 * Props:
 *   lat / lon — current string values (may be empty)
 *   onChange  — called with updated lat/lon strings on every pin move
 */

import React, { useEffect, useRef, useCallback } from 'react';
import L from 'leaflet';
import styles from './ProjectStepper.module.css';

interface LocationPickerMapProps {
  lat: string;
  lon: string;
  onChange?: (lat: string, lon: string) => void;
  /** Read-only: show the pin, allow pan/zoom, but no click/drag editing. */
  readOnly?: boolean;
}

/* ── Helpers ─────────────────────────────────────────────── */

const fmt = (v: number) =>
  (Math.round(v * 1_000_000) / 1_000_000).toString();

/** Normalize longitude to [-180, 180]. */
const normLon = (lon: number) =>
  ((((lon + 180) % 360) + 360) % 360) - 180;

const parseCoord = (s: string): number | null => {
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : null;
};

const makePinIcon = () =>
  L.divIcon({
    className: '',
    html: '<div style="width:18px;height:18px;border-radius:50%;background:#38bdf8;border:2.5px solid rgba(255,255,255,0.95);box-shadow:0 4px 12px rgba(0,0,0,0.4);"></div>',
    iconSize: [18, 18],
    iconAnchor: [9, 9],
  });

/* ── Component ───────────────────────────────────────────── */

export default function LocationPickerMap({ lat, lon, onChange, readOnly = false }: LocationPickerMapProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markerRef = useRef<L.Marker | null>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Stable callback — writes formatted lat/lon to parent
  const emitLatLon = useCallback((latN: number, lonN: number) => {
    onChangeRef.current?.(fmt(latN), fmt(normLon(lonN)));
  }, []);

  // ── Mount / unmount the Leaflet instance ────────────────
  useEffect(() => {
    if (!containerRef.current) return;

    const parsedLat = parseCoord(lat);
    const parsedLon = parseCoord(lon);
    const hasCoords = parsedLat !== null && parsedLon !== null;
    const center: [number, number] = hasCoords
      ? [parsedLat!, parsedLon!]
      : [30, 0];
    const zoom = hasCoords ? 10 : 2;

    // Theme detection
    const isDark = document.documentElement.getAttribute('data-theme') !== 'light';

    const map = L.map(containerRef.current, {
      zoomControl: true,
      attributionControl: false,
    }).setView(center, zoom);

    // Tile layer — theme-aware
    if (isDark) {
      // Esri World Imagery
      L.tileLayer(
        'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
        { maxZoom: 18 }
      ).addTo(map);
      // Labels overlay
      L.tileLayer(
        'https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}',
        { maxZoom: 18 }
      ).addTo(map);
    } else {
      L.tileLayer(
        'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',
        { maxZoom: 19 }
      ).addTo(map);
    }

    // Marker — only show if coords exist
    const marker = L.marker(center, {
      draggable: !readOnly,
      icon: makePinIcon(),
      opacity: hasCoords ? 1 : 0,
    }).addTo(map);

    if (!readOnly) {
      marker.on('dragend', () => {
        const ll = marker.getLatLng();
        emitLatLon(ll.lat, ll.lng);
      });

      map.on('click', (e: any) => {
        const ll = e?.latlng;
        if (!ll) return;
        marker.setLatLng(ll);
        marker.setOpacity(1);
        emitLatLon(ll.lat, ll.lng);
      });
    }

    mapRef.current = map;
    markerRef.current = marker;

    // Leaflet needs a tick to measure container size
    setTimeout(() => map.invalidateSize(), 0);

    return () => {
      map.remove();
      mapRef.current = null;
      markerRef.current = null;
    };
    // Only mount once — parent lat/lon sync is handled by the next effect
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Sync marker when parent lat/lon changes (typed input) ─
  useEffect(() => {
    if (!mapRef.current || !markerRef.current) return;
    const pLat = parseCoord(lat);
    const pLon = parseCoord(lon);
    if (pLat === null || pLon === null) return;
    const ll = L.latLng(pLat, pLon);
    markerRef.current.setLatLng(ll);
    markerRef.current.setOpacity(1);
    mapRef.current.panTo(ll, { animate: false });
  }, [lat, lon]);

  return (
    <div className={styles.formGroup}>
      <div className={styles.locationMapWrap}>
        <div ref={containerRef} className={styles.locationMap} />
      </div>
    </div>
  );
}
